import { useState, useEffect } from "react";
import {
    Modal,
    Platform,
    ActivityIndicator,
    ScrollView,
    View,
    Alert,
    Pressable,
    KeyboardAvoidingView,
    StyleSheet,
} from "react-native";
import {
    collection,
    doc,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    arrayRemove,
    arrayUnion,
    serverTimestamp,
    where,
    query,
    orderBy,
} from "firebase/firestore";
import { firestore } from "../../../services/firebase";
import AdminInput from "../../../components/AdminInput";
import AdminSwitch from "../../../components/AdminSwitch";
import { Text } from "react-native";
import { Ionicons, MaterialIcons } from '@expo/vector-icons'
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";
import {
    SegmentedControl,
    Badge,
    useModalWidth,
    useResponsive,
} from "../Products/formStyles";
import { useRemoteConfigContext } from "../../../contexts/useRemoteConfigContext";
import {
    buildGlobalCatalogFiltersAiAssistantText,
    copyTextToClipboard,
} from "../../../utils/buildGlobalCatalogFiltersAiAssistantText";

export default function AdminFiltersModal({ visible, onClose, onRefreshCategories }) {
    const modalWidth = useModalWidth();
    const { isMobile } = useResponsive();
    const { hasProductCatalog } = useRemoteConfigContext();

    const [loadingTree, setLoadingTree] = useState(true);
    const [saving, setSaving] = useState(false);

    const [settings, setSettings] = useState({
        prefilterByTagFirst: false,
    });

    const [tree, setTree] = useState([]);
    const [newName, setNewName] = useState({ value: "", error: "" });
    const [newOrder, setNewOrder] = useState({ value: "", error: "" });
    const [showCreate, setShowCreate] = useState(false);

    const [filtersList, setFiltersList] = useState([]);
    const [editingFilter, setEditingFilter] = useState(null);
    const [isEditingCatsAndCards, setIsEditingCatsAndCards] = useState(false);
    const [associatedCats, setAssociatedCats] = useState([]);
    const [associatedCards, setAssociatedCards] = useState([]);
    const [expandedFilterId, setExpandedFilterId] = useState(null)
    const [expandedCategoryIds, setExpandedCategoryIds] = useState([]);
    const [aiPromptFeedback, setAiPromptFeedback] = useState('');

    // reset form
    function resetForm() {
        setNewName({ value: "", error: "" });
        setNewOrder({ value: "", error: "" });
        setShowCreate(false);
        setTree(old =>
            old.map(cat => ({
                ...cat,
                selected: false,
                cards: cat.cards.map(cd => ({ ...cd, selected: false }))
            }))
        );
    }

    useEffect(() => {
        if (!visible) return;
        (async () => {
            setLoadingTree(true);
            // fetch general settings
            try {
                const settingsSnap = await getDoc(doc(firestore, "customization", "settings"));
                if (settingsSnap.exists()) {
                    const data = settingsSnap.data();
                    setSettings({
                        prefilterByTagFirst: data.prefilterByTagFirst ?? false,
                    });
                }
            } catch (err) {
                console.error("Erro ao buscar settings:", err);
            }
            // fetch categories+cards tree, já ordenando
            try {
                const catsSnap = await getDocs(
                    query(
                        collection(firestore, "categories"),
                        orderBy("order", "asc")
                    )
                );
                const tmp = await Promise.all(
                    catsSnap.docs.map(async catDoc => {
                        const cat = {
                            id: catDoc.id,
                            title: catDoc.data().title,
                            selected: false,
                            cards: []
                        };
                        const cardsSnap = await getDocs(
                            query(
                                collection(firestore, "categories", cat.id, "cards"),
                                orderBy("order", "asc")
                            )
                        );
                        cardsSnap.docs.forEach(cdoc => {
                            cat.cards.push({
                                id: cdoc.id,
                                title: cdoc.data().title,
                                selected: false
                            });
                        });
                        return cat;
                    })
                );
                setTree(tmp);
            } catch (err) {
                console.error("Erro ao carregar categorias:", err);
            }
            // fetch existing filters
            try {
                const q = query(
                    collection(firestore, 'filters'),
                    orderBy('order', 'asc')
                );
                const snap = await getDocs(q);
                const clean = snap.docs.map(docSnap => {
                    const d = docSnap.data();
                    return {
                        id: docSnap.id,
                        name: d.name,
                        slug: d.slug,
                        order: d.order,
                        archived: d.archived
                    };
                });

                setFiltersList(clean);
            } catch (err) {
                console.error('Erro ao buscar filtros:', err);
            } finally {
                setLoadingTree(false);
            }
        })();
    }, [visible]);

    // Quando o admin seleciona um filtro, busca categorias e cards associados
    useEffect(() => {
        if (!editingFilter) return;
        (async () => {
            // 1) ler o próprio documento do filtro
            const fRef = doc(firestore, 'filters', editingFilter.id);
            const fSnap = await getDoc(fRef);
            if (!fSnap.exists()) return;

            const { categoryIds = [], cardEntries = [] } = fSnap.data();

            // 2) buscar títulos das categorias
            const cats = await Promise.all(
                categoryIds.map(async catId => {
                    const cSnap = await getDoc(doc(firestore, 'categories', catId));
                    return { id: catId, title: cSnap.data()?.title ?? '' };
                })
            );
            setAssociatedCats(cats);

            // 3) buscar títulos dos cards
            const cards = await Promise.all(
                cardEntries.map(async ({ categoryId, cardId }) => {
                    const cardSnap = await getDoc(
                        doc(firestore, 'categories', categoryId, 'cards', cardId)
                    );
                    return {
                        categoryId,
                        cardId,
                        title: cardSnap.data()?.title ?? ''
                    };
                })
            );
            setAssociatedCards(cards);
            setTree(prevTree =>
                prevTree.map(cat => {
                    const isCatSelected = categoryIds.includes(cat.id);
                    return {
                        ...cat,
                        selected: isCatSelected,
                        cards: cat.cards.map(card => {
                            const isCardSelected = cardEntries.some(
                                entry => entry.cardId === card.id && entry.categoryId === cat.id
                            );
                            return {
                                ...card,
                                selected: isCatSelected ? true : isCardSelected,
                            };
                        }),
                    };
                })
            );

        })();
    }, [editingFilter]);

    // arquivar / desarquivar
    async function toggleArchive(editingFilter) {
        const ref = doc(firestore, "filters", editingFilter.id);
        await updateDoc(ref, { archived: !editingFilter.archived });
        setFiltersList(list =>
            list.map(f =>
                f.id === editingFilter.id ? { ...f, archived: !f.archived } : f
            )
        );
        setEditingFilter(f => (f && f.id === editingFilter.id ? { ...f, archived: !f.archived } : f));
    }

    async function handleDeleteFilter(filterId) {
        try {
            const filterRef = doc(firestore, 'filters', filterId);
            const filterSnap = await getDoc(filterRef);
            if (!filterSnap.exists()) return;

            const { slug, categoryIds = [], cardEntries = [] } = filterSnap.data();
            const promises = [];

            // remover slug de cada categoria existente
            for (const catId of categoryIds) {
                const catRef = doc(firestore, 'categories', catId);
                const catSnap = await getDoc(catRef);
                if (catSnap.exists()) {
                    promises.push(
                        updateDoc(catRef, { filterIds: arrayRemove(slug) })
                    );
                }
            }

            // remover slug de cada card existente
            for (const { categoryId, cardId } of cardEntries) {
                const cardRef = doc(firestore, 'categories', categoryId, 'cards', cardId);
                const cardSnap = await getDoc(cardRef);
                if (cardSnap.exists()) {
                    promises.push(
                        updateDoc(cardRef, { filterIds: arrayRemove(slug) })
                    );
                }
            }

            // aguarda todas as remoções válidas
            await Promise.all(promises);

            // por fim, deleta o próprio filtro
            await deleteDoc(filterRef);

            // atualiza estado local
            setFiltersList(list => list.filter(f => f.id !== filterId));
            setEditingFilter(null);
        } catch (err) {
            Alert.alert('Erro', 'Não foi possível excluir o filtro.');
        }
    }

    async function handleSaveAll() {
        const trimmedName = newName.value.trim();

        // MODO EDIÇÃO DE FILTRO EXISTENTE
        if (editingFilter) {
            const filterRef = doc(firestore, 'filters', editingFilter.id);
            const oldSnap = await getDoc(filterRef);
            const { slug, categoryIds: oldCats = [], cardEntries: oldCards = [] } = oldSnap.data();

            const nameRegex = /^[\p{L}0-9]+(?: [\p{L}0-9]+){0,2}$/u;
            if (!nameRegex.test(trimmedName)) {
                setNewName({ ...newName, error: "Digite até 3 palavras sem caracteres especiais." });
                return;
            }

            const orderNum = parseInt(newOrder.value, 10);
            if (isNaN(orderNum)) {
                setNewOrder({ ...newOrder, error: "Informe um número válido para a ordem." });
                return;
            }

            // Novas seleções feitas no tree
            const selectedCategoryIds = tree
                .filter(cat => cat.selected)
                .map(cat => cat.id);

            const selectedCardEntries = tree.flatMap(cat =>
                cat.selected
                    ? []
                    : cat.cards
                        .filter(card => card.selected)
                        .map(card => ({
                            categoryId: cat.id,
                            cardId: card.id,
                        }))
            );

            const batch = [];

            // Remover slug de categorias e cards antigos
            oldCats.forEach(catId => {
                batch.push(
                    updateDoc(doc(firestore, 'categories', catId), {
                        filterIds: arrayRemove(slug),
                    })
                );
            });
            oldCards.forEach(({ categoryId, cardId }) => {
                batch.push(
                    updateDoc(doc(firestore, 'categories', categoryId, 'cards', cardId), {
                        filterIds: arrayRemove(slug),
                    })
                );
            });

            // Adicionar slug nas novas categorias/cards
            selectedCategoryIds.forEach(catId => {
                batch.push(
                    updateDoc(doc(firestore, 'categories', catId), {
                        filterIds: arrayUnion(slug),
                    })
                );
            });
            selectedCardEntries.forEach(({ categoryId, cardId }) => {
                batch.push(
                    updateDoc(doc(firestore, 'categories', categoryId, 'cards', cardId), {
                        filterIds: arrayUnion(slug),
                    })
                );
            });

            // Atualiza o filtro com as novas associações
            await updateDoc(filterRef, {
                categoryIds: selectedCategoryIds,
                cardEntries: selectedCardEntries,
                name: trimmedName,
                order: orderNum,
                updatedAt: serverTimestamp(),
            });

            await Promise.all(batch);
            if (typeof onRefreshCategories === 'function') {
                await onRefreshCategories();
            }
            resetForm();
            setIsEditingCatsAndCards(false);
            onClose();
            return;
        }

        // MODO CRIAÇÃO DE NOVO FILTRO
        // Se o trimmedName estiver vazio, salva apenas configurações gerais
        if (!trimmedName) {
            setSaving(true);
            try {
                await setDoc(
                    doc(firestore, "customization", "settings"),
                    { prefilterByTagFirst: settings.prefilterByTagFirst, updatedAt: serverTimestamp() },
                    { merge: true }
                );
                onClose();
            } catch (e) {
                console.error("Erro ao salvar settings:", e);
            } finally {
                setSaving(false);
            }
            return;
        }

        const nameRegex = /^[\p{L}0-9]+(?: [\p{L}0-9]+){0,2}$/u;

        if (!nameRegex.test(trimmedName)) {
            setNewName({ ...newName, error: "Digite até 3 palavras sem caracteres especiais." });
            return;
        }
        // prevent duplicate
        const existsQ = query(
            collection(firestore, "filters"),
            where("name", "==", trimmedName)
        );
        const existsSnap = await getDocs(existsQ);
        if (!existsSnap.empty) {
            setNewName({ ...newName, error: "Já existe um filtro com esse nome." });
            return;
        }
        const orderNum = parseInt(newOrder.value, 10);
        if (isNaN(orderNum)) {
            setNewOrder({ ...newOrder, error: "Informe um número válido para a ordem." });
            return;
        }

        setSaving(true);
        try {
            // save general settings
            await setDoc(
                doc(firestore, "customization", "settings"),
                { prefilterByTagFirst: settings.prefilterByTagFirst, updatedAt: serverTimestamp() },
                { merge: true }
            );
            // create new filter
            const slug = trimmedName
                // 1) separa letras + seus acentos
                .normalize("NFD")
                // 2) remove os diacríticos (acentos)
                .replace(/[\u0300-\u036f]/g, "")
                // 3) abaixa tudo
                .toLowerCase()
                // 4) espaços viram underscore
                .replace(/\s+/g, "_")
                // 5) remove qualquer coisa que não seja letra a–z, número ou _
                .replace(/[^a-z0-9_]/g, "");

            // extrai só as categorias marcadas
            const selectedCategoryIds = tree
                .filter(cat => cat.selected)
                .map(cat => cat.id);

            // extrai só os cards marcados (dentro de cada categoria não totalmente marcada)
            const selectedCardEntries = tree
                .flatMap(cat =>
                    cat.selected
                        ? []       // se toda a categoria já foi marcada, não precisamos repetir os cards
                        : cat.cards
                            .filter(card => card.selected)
                            .map(card => ({
                                categoryId: cat.id,
                                cardId: card.id,
                            }))
                );

            const filterRef = doc(collection(firestore, "filters"));
            await setDoc(filterRef, {
                name: trimmedName,
                slug,
                order: orderNum,
                archived: false,
                createdAt: serverTimestamp(),
                categoryIds: selectedCategoryIds,
                cardEntries: selectedCardEntries
            }, { merge: true });
            // associate filter with selected categories/cards
            const batch = [];
            tree.forEach(cat => {
                if (cat.selected) {
                    batch.push(
                        updateDoc(doc(firestore, "categories", cat.id), {
                            filterIds: arrayUnion(slug)
                        })
                    );
                } else {
                    cat.cards.forEach(card => {
                        if (card.selected) {
                            batch.push(
                                updateDoc(
                                    doc(firestore, "categories", cat.id, "cards", card.id),
                                    { filterIds: arrayUnion(slug) }
                                )
                            );
                        }
                    });
                }
            });
            await Promise.all(batch);
            if (typeof onRefreshCategories === 'function') {
                await onRefreshCategories();
              }
            resetForm();
            onClose();
        } catch (e) {
            Alert.alert("Erro", "Não foi possível salvar os filtros. Tente novamente.");
        } finally {
            setSaving(false);
        }
    }

    // Fecha só o formulário de criação/edição, mantendo o modal aberto.
    function closeFilterForm() {
        resetForm(); // limpa nome, ordem e a seleção da árvore
        setIsEditingCatsAndCards(false);
        setEditingFilter(null);
    }

    function handleCancel() {
        closeFilterForm();
        onClose();
    }

    const handleGenerateWithAi = async () => {
        if (!tree.length) return;

        const text = buildGlobalCatalogFiltersAiAssistantText(tree, filtersList, {
            prefilterByTagFirst: settings.prefilterByTagFirst,
        });
        try {
            const copied = await copyTextToClipboard(text);
            setAiPromptFeedback(
                copied
                    ? 'Prompt copiado! Cole na IA da sua preferência (ChatGPT, Claude ou similar).'
                    : 'Não foi possível copiar. Tente novamente no navegador.'
            );
        } catch (error) {
            console.error('Erro ao copiar prompt de filtros:', error);
            setAiPromptFeedback('Não foi possível copiar. Tente novamente no navegador.');
        }

        setTimeout(() => setAiPromptFeedback(''), 5000);
    };

    if (!visible) return null;

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={handleCancel}>
            <View style={styles.overlay}>
                <KeyboardAvoidingView
                    behavior={Platform.OS === "ios" ? "padding" : undefined}
                    style={[styles.card, { width: modalWidth, maxWidth: 720 }]}
                >
                    <View style={styles.header}>
                        <View style={{ flex: 1, minWidth: 0 }}>
                            <Text style={styles.title}>Filtros do catálogo</Text>
                            <Text style={styles.subtitle}>
                                Como o catálogo abre para o aluno e quais filtros ficam disponíveis.
                                Use Gerar com IA para copiar a estrutura do catálogo e um prompt para
                                colar na IA da sua preferência (ChatGPT, Claude ou similar) e receber sugestões
                                de filtros e quais categorias/módulos vincular.
                            </Text>
                            {aiPromptFeedback ? (
                                <Text style={styles.aiPromptFeedback}>{aiPromptFeedback}</Text>
                            ) : null}
                        </View>
                        {!loadingTree && tree.length > 0 ? (
                            <Pressable
                                onPress={handleGenerateWithAi}
                                style={({ hovered }) => [
                                    styles.generateWithAiBtn,
                                    hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                ]}
                            >
                                <MaterialIcons
                                    name="auto-awesome"
                                    size={16}
                                    color={ADMIN_COLORS.accent}
                                />
                                <Text style={styles.generateWithAiBtnText}>Gerar com IA</Text>
                            </Pressable>
                        ) : null}
                        <Pressable
                            onPress={handleCancel}
                            hitSlop={8}
                            style={({ hovered }) => [
                                styles.closeBtn,
                                hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                            ]}
                        >
                            <Ionicons name="close" size={20} color={ADMIN_COLORS.muted} />
                        </Pressable>
                    </View>

                    <ScrollView
                        style={{ maxHeight: '62vh' }}
                        contentContainerStyle={{ padding: 20 }}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator
                    >
                        {loadingTree ? (
                            <View style={styles.loadingBox}>
                                <ActivityIndicator size="large" color={ADMIN_COLORS.accent} />
                            </View>
                        ) : (
                            <>
                                {/* Escolhas mutuamente exclusivas: os pares de checkbox
                                    sugeriam seleção múltipla, e o texto "Resultado: …"
                                    repetia o que a opção já diz. */}
                                <Text style={styles.sectionTitle}>Ao abrir o app</Text>
                                <SegmentedControl
                                    value={settings.prefilterByTagFirst ? 'prefilter' : 'full'}
                                    onChange={(value) =>
                                        setSettings(s => ({ ...s, prefilterByTagFirst: value === 'prefilter' }))
                                    }
                                    options={[
                                        {
                                            value: 'prefilter',
                                            label: 'Abrir pré-filtrado',
                                            description: 'O catálogo já abre com o primeiro filtro aplicado.',
                                            icon: 'funnel-outline',
                                        },
                                        {
                                            value: 'full',
                                            label: 'Abrir catálogo completo',
                                            description: 'O aluno vê tudo e escolhe o filtro.',
                                            icon: 'grid-outline',
                                        },
                                    ]}
                                />

                                {hasProductCatalog && (
                                    <View style={styles.vitrineHintBox}>
                                        <Ionicons
                                            name="information-circle-outline"
                                            size={18}
                                            color={ADMIN_COLORS.accent}
                                        />
                                        <Text style={styles.vitrineHintText}>
                                            Com a vitrine ativa, configure os filtros de módulos
                                            em cada produto na aba Produtos. Este painel continua
                                            valendo para quem não usa vitrine ou como fallback
                                            enquanto o produto ainda não tem filtros próprios.
                                        </Text>
                                    </View>
                                )}

                                {filtersList.length > 0 && (
                                    <View style={{ marginTop: 24 }}>
                                        <Text style={styles.sectionTitle}>Filtros existentes</Text>

                                        <View style={styles.listBox}>
                                            {filtersList.map((filter, index) => {
                                                const isExpanded = expandedFilterId === filter.id
                                                const isPublished = !filter.archived

                                                return (
                                                    <View
                                                        key={filter.id}
                                                        style={index < filtersList.length - 1 ? styles.listDivider : null}
                                                    >
                                                        {/* cabeçalho da linha */}
                                                        <View style={styles.filterRow}>
                                                            <Pressable
                                                                onPress={() => {
                                                                    if (isExpanded) {
                                                                        setExpandedFilterId(null)
                                                                        setEditingFilter(null)
                                                                    } else {
                                                                        setExpandedFilterId(filter.id)
                                                                        setEditingFilter(filter)
                                                                    }
                                                                }}
                                                                style={({ hovered }) => [
                                                                    styles.filterRowMain,
                                                                    hovered && { opacity: 0.7 },
                                                                ]}
                                                            >
                                                                <Ionicons
                                                                    name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                                                                    size={16}
                                                                    color={ADMIN_COLORS.muted}
                                                                />
                                                                <Text style={styles.filterName} numberOfLines={1}>
                                                                    {filter.name}
                                                                </Text>
                                                            </Pressable>

                                                            <Badge variant={isPublished ? 'success' : 'neutral'}>
                                                                {isPublished ? 'Publicado' : 'Arquivado'}
                                                            </Badge>

                                                            <AdminSwitch
                                                                value={isPublished}
                                                                onValueChange={() => toggleArchive(filter)}
                                                            />
                                                        </View>

                                                        {/* área expandida */}
                                                        {isExpanded && (
                                                            <View style={styles.expanded}>
                                                                <Text style={styles.expandedLabel}>
                                                                    Conteúdos vinculados
                                                                </Text>
                                                                {associatedCats.length === 0 && associatedCards.length === 0 ? (
                                                                    <Text style={styles.expandedEmpty}>
                                                                        Nenhum conteúdo vinculado a este filtro.
                                                                    </Text>
                                                                ) : null}
                                                                {/* categorias */}
                                                                {associatedCats.map(c => (
                                                                    <Text style={styles.expandedItem} key={c.id}>
                                                                        • Categoria: {c.title}
                                                                    </Text>
                                                                ))}
                                                                {/* cards */}
                                                                {associatedCards.map(c => (
                                                                    <Text style={styles.expandedItem} key={c.cardId}>
                                                                        • Módulo: {c.title}
                                                                    </Text>
                                                                ))}

                                                                <View style={styles.expandedActions}>
                                                                    <Pressable
                                                                        onPress={() => {
                                                                            setNewName({ value: editingFilter.name, error: '' });
                                                                            setNewOrder({ value: editingFilter.order?.toString() ?? '', error: '' });
                                                                            setIsEditingCatsAndCards(true);
                                                                            setShowCreate(false);
                                                                            setExpandedFilterId(null);
                                                                        }}
                                                                        style={({ hovered }) => [
                                                                            styles.ghostBtn,
                                                                            hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                                                        ]}
                                                                    >
                                                                        <Ionicons name="create-outline" size={16} color={ADMIN_COLORS.ink} />
                                                                        <Text style={styles.ghostBtnText}>Editar</Text>
                                                                    </Pressable>
                                                                    <Pressable
                                                                        onPress={() => handleDeleteFilter(filter.id)}
                                                                        style={({ hovered }) => [
                                                                            styles.ghostBtn,
                                                                            { borderColor: ADMIN_COLORS.dangerBorder },
                                                                            hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
                                                                        ]}
                                                                    >
                                                                        <Ionicons name="trash-outline" size={16} color={ADMIN_COLORS.danger} />
                                                                        <Text style={[styles.ghostBtnText, { color: ADMIN_COLORS.danger }]}>
                                                                            Excluir
                                                                        </Text>
                                                                    </Pressable>
                                                                </View>
                                                            </View>
                                                        )}
                                                    </View>
                                                )
                                            })}
                                        </View>
                                    </View>
                                )}

                                {!showCreate && !isEditingCatsAndCards && (
                                    <Pressable
                                        onPress={() => {
                                            setShowCreate(true);
                                            setIsEditingCatsAndCards(false);
                                            setEditingFilter(null);
                                            setNewOrder({
                                                value: (filtersList.length + 1).toString(),
                                                error: ""
                                            });
                                        }}
                                        disabled={loadingTree || saving}
                                        style={({ hovered }) => [
                                            styles.newFilterBtn,
                                            hovered && { backgroundColor: ADMIN_COLORS.accentSoft },
                                        ]}
                                    >
                                        <Ionicons name="add" size={18} color={ADMIN_COLORS.accent} />
                                        <Text style={styles.newFilterBtnText}>Criar novo filtro</Text>
                                    </Pressable>
                                )}

                                {(showCreate || isEditingCatsAndCards) && (
                                    <>
                                        <View style={styles.formBlock}>
                                            {/* Fecha só o formulário — o modal
                                                continua aberto. Sem isto, uma vez
                                                aberto o formulário só saía dele
                                                salvando ou fechando tudo. */}
                                            <View style={styles.formHeader}>
                                                <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>
                                                    {isEditingCatsAndCards ? 'Editar filtro' : 'Novo filtro'}
                                                </Text>
                                                <Pressable
                                                    onPress={closeFilterForm}
                                                    hitSlop={8}
                                                    accessibilityLabel="Fechar edição do filtro"
                                                    style={({ hovered }) => [
                                                        styles.closeBtn,
                                                        { width: 28, height: 28 },
                                                        hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                                    ]}
                                                >
                                                    <Ionicons name="close" size={18} color={ADMIN_COLORS.muted} />
                                                </Pressable>
                                            </View>

                                            <Text style={styles.fieldLabel}>Nome do filtro</Text>
                                            <AdminInput
                                                placeholder="Ex: Meditações"
                                                value={newName.value}
                                                onChangeText={text =>
                                                    setNewName({ value: text, error: "" })
                                                }
                                            />
                                            {newName.error ? (
                                                <Text style={styles.fieldError}>{newName.error}</Text>
                                            ) : null}

                                            <Text style={[styles.fieldLabel, { marginTop: 16 }]}>Ordem</Text>
                                            <AdminInput
                                                placeholder="Nº da ordem que o filtro deve aparecer"
                                                value={newOrder.value}
                                                keyboardType="numeric"
                                                onChangeText={text => {
                                                    const digits = text.replace(/[^0-9]/g, "");
                                                    setNewOrder({ value: digits, error: "" });
                                                }}
                                            />
                                            {newOrder.error ? (
                                                <Text style={styles.fieldError}>{newOrder.error}</Text>
                                            ) : null}

                                            <Text style={[styles.fieldLabel, { marginTop: 20 }]}>
                                                Vincular a categorias ou módulos
                                            </Text>
                                            <Text style={styles.fieldHint}>
                                                Marcar a categoria seleciona todos os módulos dela.
                                            </Text>
                                            <View style={styles.treeBox}>
                                                {tree.map(cat => {
                                                    const isExpanded = expandedCategoryIds?.includes(cat.id);
                                                    return (
                                                        <View key={cat.id}>
                                                            <View style={styles.treeRow}>
                                                                <Pressable
                                                                    hitSlop={6}
                                                                    style={styles.treeChevron}
                                                                    onPress={() => {
                                                                        setExpandedCategoryIds(prev =>
                                                                            prev?.includes(cat.id)
                                                                                ? prev.filter(id => id !== cat.id)
                                                                                : [...(prev || []), cat.id]
                                                                        );
                                                                    }}
                                                                >
                                                                    <Ionicons
                                                                        name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                                                                        size={16}
                                                                        color={ADMIN_COLORS.muted}
                                                                    />
                                                                </Pressable>
                                                                <Pressable
                                                                    style={({ hovered }) => [
                                                                        styles.treeLabelBtn,
                                                                        hovered && { opacity: 0.75 },
                                                                    ]}
                                                                    onPress={() => {
                                                                        setTree(t =>
                                                                            t.map(c =>
                                                                                c.id === cat.id
                                                                                    ? {
                                                                                        ...c,
                                                                                        selected: !c.selected,
                                                                                        cards: c.cards.map(cd => ({
                                                                                            ...cd,
                                                                                            selected: !c.selected
                                                                                        }))
                                                                                    }
                                                                                    : c
                                                                            )
                                                                        );
                                                                    }}
                                                                >
                                                                    <Ionicons
                                                                        name={cat.selected ? 'checkbox' : 'square-outline'}
                                                                        size={18}
                                                                        color={cat.selected ? ADMIN_COLORS.accent : ADMIN_COLORS.placeholder}
                                                                    />
                                                                    <Text style={styles.treeLabel} numberOfLines={1}>
                                                                        {cat.title}
                                                                    </Text>
                                                                </Pressable>
                                                            </View>

                                                            {isExpanded && cat.cards.map(cd => (
                                                                <Pressable
                                                                    key={cd.id}
                                                                    style={({ hovered }) => [
                                                                        styles.treeLabelBtn,
                                                                        styles.treeChild,
                                                                        hovered && { opacity: 0.75 },
                                                                    ]}
                                                                    onPress={() => {
                                                                        setTree(t =>
                                                                            t.map(c => {
                                                                                if (c.id !== cat.id) return c;
                                                                                const cards = c.cards.map(cc =>
                                                                                    cc.id === cd.id
                                                                                        ? { ...cc, selected: !cc.selected }
                                                                                        : cc
                                                                                );
                                                                                return {
                                                                                    ...c,
                                                                                    selected:
                                                                                        c.selected &&
                                                                                        cards.every(cc => cc.selected),
                                                                                    cards,
                                                                                };
                                                                            })
                                                                        );
                                                                    }}
                                                                >
                                                                    <Ionicons
                                                                        name={cd.selected ? 'checkbox' : 'square-outline'}
                                                                        size={16}
                                                                        color={cd.selected ? ADMIN_COLORS.accent : ADMIN_COLORS.placeholder}
                                                                    />
                                                                    <Text style={[styles.treeLabel, { fontSize: 13 }]} numberOfLines={1}>
                                                                        {cd.title}
                                                                    </Text>
                                                                </Pressable>
                                                            ))}
                                                        </View>
                                                    );
                                                })}
                                            </View>
                                        </View>
                                    </>
                                )}
                            </>
                        )}
                    </ScrollView>

                    <View style={[styles.footer, isMobile && { flexDirection: 'column-reverse' }]}>
                        <Pressable
                            onPress={handleCancel}
                            disabled={saving}
                            style={({ hovered }) => [
                                styles.btn,
                                styles.btnSecondary,
                                isMobile && { width: '100%' },
                                hovered && { backgroundColor: '#E5E7EB' },
                            ]}
                        >
                            <Text style={[styles.btnText, { color: ADMIN_COLORS.secondaryText }]}>
                                Cancelar
                            </Text>
                        </Pressable>
                        <Pressable
                            onPress={handleSaveAll}
                            disabled={saving}
                            style={({ hovered }) => [
                                styles.btn,
                                styles.btnPrimary,
                                isMobile && { width: '100%' },
                                hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                                saving && { opacity: 0.7 },
                            ]}
                        >
                            {saving ? (
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                                <Text style={[styles.btnText, { color: '#FFFFFF' }]}>Salvar</Text>
                            )}
                        </Pressable>
                    </View>
                </KeyboardAvoidingView>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(17,24,39,0.45)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
    },
    card: {
        maxHeight: '90%',
        backgroundColor: ADMIN_COLORS.surface,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        padding: 20,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: ADMIN_COLORS.border,
    },
    title: { fontFamily: ADMIN_FONT, fontSize: 18, fontWeight: '700', color: ADMIN_COLORS.ink },
    subtitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        marginTop: 4,
        lineHeight: 18,
    },
    aiPromptFeedback: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        color: ADMIN_COLORS.success,
        marginTop: 8,
        lineHeight: 17,
    },
    generateWithAiBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 10,
        backgroundColor: ADMIN_COLORS.surface,
        cursor: 'pointer',
        marginTop: 2,
    },
    generateWithAiBtnText: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        fontWeight: '600',
        color: ADMIN_COLORS.accent,
    },
    closeBtn: {
        width: 32,
        height: 32,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    loadingBox: { paddingVertical: 60, alignItems: 'center' },
    sectionTitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '700',
        color: ADMIN_COLORS.muted,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 10,
    },
    sectionHint: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        lineHeight: 17,
        color: ADMIN_COLORS.muted,
    },
    vitrineHintBox: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        padding: 12,
        marginTop: 20,
        borderRadius: 10,
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
    },
    vitrineHintText: {
        flex: 1,
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        lineHeight: 18,
        color: ADMIN_COLORS.muted,
    },
    listBox: {
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 10,
        overflow: 'hidden',
    },
    listDivider: { borderBottomWidth: 1, borderBottomColor: ADMIN_COLORS.border },
    filterRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 10,
        paddingHorizontal: 12,
    },
    filterRowMain: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
    },
    filterName: {
        flex: 1,
        minWidth: 0,
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: '600',
        color: ADMIN_COLORS.ink,
    },
    expanded: {
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
        padding: 14,
    },
    expandedLabel: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        fontWeight: '700',
        color: ADMIN_COLORS.muted,
        marginBottom: 8,
    },
    expandedItem: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.ink,
        lineHeight: 20,
    },
    expandedEmpty: { fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted },
    expandedActions: { flexDirection: 'row', gap: 8, marginTop: 12 },
    ghostBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: ADMIN_COLORS.surface,
        cursor: 'pointer',
    },
    ghostBtnText: { fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '600', color: ADMIN_COLORS.ink },
    newFilterBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        marginTop: 20,
        minHeight: 44,
        borderRadius: 10,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: ADMIN_COLORS.accent,
        cursor: 'pointer',
    },
    newFilterBtnText: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: '600',
        color: ADMIN_COLORS.accent,
    },
    formBlock: {
        marginTop: 24,
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
        paddingTop: 20,
    },
    formHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 12,
    },
    fieldLabel: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: '600',
        color: ADMIN_COLORS.label,
        marginBottom: 6,
    },
    fieldHint: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        color: ADMIN_COLORS.muted,
        marginTop: -2,
        marginBottom: 8,
        lineHeight: 16,
    },
    fieldError: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        color: ADMIN_COLORS.danger,
        marginTop: 6,
        fontWeight: '500',
    },
    treeBox: {
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 10,
        paddingVertical: 6,
        paddingHorizontal: 8,
    },
    treeRow: { flexDirection: 'row', alignItems: 'center' },
    treeChevron: { width: 24, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    treeLabelBtn: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 7,
        cursor: 'pointer',
    },
    treeChild: { paddingLeft: 32 },
    treeLabel: {
        flex: 1,
        minWidth: 0,
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        color: ADMIN_COLORS.ink,
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
        padding: 20,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
    },
    btn: {
        minHeight: 44,
        borderRadius: 10,
        paddingHorizontal: 22,
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    btnPrimary: { backgroundColor: ADMIN_COLORS.accent },
    btnSecondary: { backgroundColor: ADMIN_COLORS.secondaryBg },
    btnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600' },
});
